import React from "react";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TransformIcon from "@mui/icons-material/Transform";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import ReportIcon from "@mui/icons-material/Report";
import PaymentIcon from "@mui/icons-material/Payment";
import SupportIcon from "@mui/icons-material/Support";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalculateIcon from "@mui/icons-material/Calculate";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { getUserProfile } from "../jwt";
import Dashboard from "../pages/Dashboard";
import Assets from "../pages/Assets";
import Users from "../pages/Users";
import OwnershipRequests from "../pages/OwnershipRequests";
import TransferRequests from "../pages/TransferRequests";
import UserProfileChangeRequests from "../pages/UserProfileChangeRequests";
import AssetReports from "../pages/AssetReports";
import Payments from "../pages/Payments";
import SupportTickets from "../pages/SupportTickets";
import FieldOfficialOverview from "../pages/FieldOfficialOverview";
import LicenseFeeCalculations from "../pages/LicenseFeeCalculations";
import Rewards from "../pages/Rewards";

export interface SidebarItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  allowedRoles: string[];
  element: React.ReactNode;
}

export const useSidebarItems = () => {
  const userProfile = getUserProfile();
  const userRole = userProfile?.currentRole;

  const allItems: SidebarItem[] = [
    {
      title: "Dashboard",
      path: "/",
      icon: <HomeRoundedIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <Dashboard />,
    },
    {
      title: "Rewards",
      path: "/rewards",
      icon: <EmojiEventsIcon />,
      allowedRoles: ["SUPER_USER"],
      element: <Rewards />,
    },
    {
      title: "Assets",
      path: "/asset-list",
      icon: <ListAltIcon />,
      allowedRoles: ["BACK_OFFICE_OFFICIAL", "SUPER_USER"],
      element: <Assets />,
    },
    {
      title: "Users",
      path: "/users",
      icon: <GroupIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <Users />,
    },
    {
      title: "Ownership Requests",
      path: "/ownership-requests",
      icon: <AssignmentIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <OwnershipRequests />,
    },
    {
      title: "Transfer Requests",
      path: "/transfer-requests",
      icon: <TransformIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_SERVICE_SUPPORT",
        "SUPER_USER",
      ],
      element: <TransferRequests />,
    },
    {
      title: "User Profile Change Requests",
      path: "/user-profile-change-requests",
      icon: <ContactPageIcon />,
      allowedRoles: ["BACK_OFFICE_OFFICIAL", "SUPER_USER"],
      element: <UserProfileChangeRequests />,
    },
    {
      title: "Asset Reports",
      path: "/asset-reports",
      icon: <ReportIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <AssetReports />,
    },
    {
      title: "Payments",
      path: "/payments",
      icon: <PaymentIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <Payments />,
    },
    {
      title: "Support Tickets",
      path: "/support-tickets",
      icon: <SupportIcon />,
      allowedRoles: [
        "BACK_OFFICE_OFFICIAL",
        "SYSTEM_ADMINISTRATOR",
        "SUPER_USER",
      ],
      element: <SupportTickets />,
    },
    {
      title: "Field Official Overview",
      path: "/field-official-overview",
      icon: <BarChartIcon />,
      allowedRoles: [
        "SYSTEM_SUPPORT",
        "SUPER_USER",
        "SYSTEM_ADMINISTRATOR",
        "SYSTEM_SERVICE_SUPPORT",
      ],
      element: <FieldOfficialOverview />,
    },
    {
      title: "License Fee Calculations",
      path: "/license-fee-calculations",
      icon: <CalculateIcon />,
      allowedRoles: ["SUPER_USER"],
      element: <LicenseFeeCalculations />,
    },
  ];

  const filterItemsByRole = (items: SidebarItem[]): SidebarItem[] => {
    return items.filter((item) => item.allowedRoles.includes(userRole));
  };

  return filterItemsByRole(allItems);
};
